"use client";

import { useState, useEffect, useRef, useId, useMemo } from "react";
import dynamic from "next/dynamic";
import { format, addDays, parseISO, startOfToday, max } from "date-fns";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useCountries } from "@/contexts/CountriesContext";
import AgreementModal from "./AgreementModal";
import DatePickerCustom from "./DatePickerCustom";
import { PhoneInput } from "react-international-phone";
import {
    loadRecaptchaScript,
    renderRecaptchaWidget,
    resetRecaptchaWidget,
    getRecaptchaToken,
    loadRecaptchaV3Script,
    getRecaptchaV3Token,
    loadTurnstileScript,
    renderTurnstileWidget,
    resetTurnstileWidget,
    getTurnstileToken,
    removeTurnstileWidget,
} from "@/lib/captcha";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import { useFormSubmitReadiness } from "@/hooks/useFormSubmitReadiness";
import { validateDynamicFormFields } from "@/utils/form-fields-validation";
import RecaptchaLegalNotice from "@/components/ui/RecaptchaLegalNotice";
import "react-international-phone/style.css";

const ReactSelect = dynamic(() => import("react-select"), { ssr: false });
import { components } from "react-select";
import type { InputProps, GroupBase } from "react-select";

/** Tarayıcı otomatik doldurmasını kapatır; uygulama dropdown'ının üstte kalması için. */
const NoAutocompleteInput = (props: InputProps<unknown, false, GroupBase<unknown>>) => {
    const uniqueId = useId();
    const { onFocus, readOnly: _ro, ...rest } = props;
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.removeAttribute("readonly");
        onFocus?.(e);
    };
    return (
        <components.Input
            {...rest}
            onFocus={handleFocus}
            autoComplete="nope"
            aria-autocomplete="list"
            data-lpignore="true"
            data-form-type="other"
            name={`country-residence-${uniqueId.replace(/:/g, "")}`}
            readOnly
        />
    );
};

const YACHT_CAPTCHA_CONTAINER_ID = "yacht-request-form-captcha-container";

interface AgreementItem {
    id: number;
    title: string;
    slug: string;
    content?: string;
    is_required: boolean;
}

interface FormAgreements {
    required: AgreementItem[];
    optional: AgreementItem[];
}

interface FormFieldValue {
    value?: string;
    label?: string;
    selected?: boolean;
}
interface FormField {
    type?: string;
    name?: string;
    field?: string;
    label?: string;
    title?: string;
    placeholder?: string;
    required?: boolean | string | number;
    rows?: number;
    values?: FormFieldValue[];
    options?: Array<string | { value: string; label: string }>;
}

interface FormDetails {
    id: number;
    title: string;
    slug: string;
    language?: { id?: number; name?: string; code?: string };
    agreements: FormAgreements;
    fields?: FormField[];
}

interface CountryOption {
    id: number;
    name: string;
}

interface YachtRequestFormProps {
    productId: number;
    productType: string;
    minRentalDuration?: number | null;
    periodStartDate?: string | null;
    periodEndDate?: string | null;
    /** Kiralama tipi: daily | weekly | monthly; talep formunda "haftalık kiralama" vb. bilgilendirme */
    rentalType?: string;
    language?: string;
    languageId?: number;
    translations?: Record<string, string>;
    className?: string;
    initialFormDetails?: FormDetails | null;
}

const labelClass = "font-semibold text-sm text-alsero-dark-gray mb-1.5 block";
const labelClassSmall = "font-semibold text-xs text-alsero-dark-gray mb-1.5 block";
const inputClass = "w-full border border-[rgba(0,0,0,0.08)] focus:border-alsero-first/40 focus:outline-none focus:ring-1 focus:ring-alsero-first/20 rounded-lg px-4 py-3 bg-white text-alsero-dark-gray text-sm transition-colors placeholder:text-gray-400";

const searchableSelectStyles = {
    control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
        ...base,
        borderColor: state.isFocused ? "rgba(227, 30, 36, 0.4)" : "rgba(0,0,0,0.08)",
        borderRadius: "0.5rem",
        minHeight: "48px",
        boxShadow: "none",
        fontSize: "0.875rem",
        "&:hover": { borderColor: "rgba(0,0,0,0.15)" },
    }),
    option: (base: Record<string, unknown>, state: { isSelected?: boolean; isFocused?: boolean }) => ({
        ...base,
        backgroundColor: state.isSelected ? "#E31E24" : state.isFocused ? "#fef2f2" : "white",
        color: state.isSelected ? "white" : "#4F545A",
        fontSize: "0.875rem",
        padding: "10px 16px",
    }),
    singleValue: (base: Record<string, unknown>) => ({ ...base, color: "#4F545A" }),
    placeholder: (base: Record<string, unknown>) => ({ ...base, color: "#9ca3af" }),
};

export default function YachtRequestForm({ productId, productType, minRentalDuration = null, periodStartDate = null, periodEndDate = null, rentalType, language = "tr", languageId, translations = {}, className = "", initialFormDetails = null }: YachtRequestFormProps) {
    const { getVariable } = useLanguageVariables();
    const t = (key: string, fallback = "") => translations[key] || getVariable(key, fallback);

    const [formDetails, setFormDetails] = useState<FormDetails | null>(initialFormDetails ?? null);
    const [formDetailsLoading, setFormDetailsLoading] = useState(!initialFormDetails);
    const [formDetailsError, setFormDetailsError] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>({});
    const [selectedAgreement, setSelectedAgreement] = useState<AgreementItem | null>(null);
    const [agreementModalOpen, setAgreementModalOpen] = useState(false);
    const [checkInDate, setCheckInDate] = useState<Date | undefined>(undefined);
    const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(undefined);
    const [guestCount, setGuestCount] = useState(1);
    const countries = useCountries();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const captchaWidgetIdRef = useRef<number | string | null>(null);
    const { config: captchaConfig, isLoading: captchaConfigLoading } = useCaptchaConfig();
    const isCaptchaRequired = Boolean(captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider);

    const minDays = minRentalDuration != null && minRentalDuration > 0 ? minRentalDuration : 1;
    const checkOutMinDate = checkInDate ? addDays(checkInDate, minDays) : undefined;

    // Takvim sınırlamaları: dönem tarihleri (periodStartDate, periodEndDate) + bugün
    const today = startOfToday();
    const periodStart = periodStartDate ? parseISO(periodStartDate) : null;
    const periodEnd = periodEndDate ? parseISO(periodEndDate) : null;
    const checkInMinDate = periodStart ? max([today, periodStart]) : today;
    const checkInDisabled: Array<{ before: Date } | { after: Date }> = [{ before: checkInMinDate }];
    if (periodEnd) checkInDisabled.push({ after: periodEnd });
    const checkOutDisabled: Array<{ before: Date } | { after: Date }> = [{ before: checkOutMinDate ?? checkInDate ?? today }];
    if (periodEnd) checkOutDisabled.push({ after: periodEnd });

    // Form detayı – t/initialFormDetails objesi dependency'de olmasın (sonsuz döngüyü önler)
    const initialFormId = initialFormDetails?.id ?? null;
    useEffect(() => {
        if (initialFormDetails) {
            setFormDetails(initialFormDetails);
            setFormDetailsLoading(false);
            setFormDetailsError(null);
            const next: Record<number, boolean> = {};
            (initialFormDetails?.agreements?.required || []).forEach((a) => { next[a.id] = true; });
            (initialFormDetails?.agreements?.optional || []).forEach((a) => { next[a.id] = false; });
            setAgreementsState(next);
            return;
        }
        const params = new URLSearchParams({ product_id: String(productId), product_type: productType });
        if (languageId) params.set("language_id", String(languageId));
        const controller = new AbortController();
        loadingTimeoutRef.current = setTimeout(() => setFormDetailsLoading(true), 200);
        fetch(`/api/forms/details?${params.toString()}`, { signal: controller.signal })
            .then((r) => r.json())
            .then((payload) => {
                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                if (!payload?.success || !payload?.data) {
                    setFormDetails(null);
                    setFormDetailsError(payload?.message || getVariable("request_form.error_form_not_found", "Form bulunamadı"));
                    return;
                }
                const details = payload.data as FormDetails;
                setFormDetails(details);
                setFormDetailsError(null);
                const next: Record<number, boolean> = {};
                (details?.agreements?.required || []).forEach((a) => { next[a.id] = true; });
                (details?.agreements?.optional || []).forEach((a) => { next[a.id] = false; });
                setAgreementsState(next);
            })
            .catch((err) => {
                if (err.name !== "AbortError" && loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                setFormDetails(null);
                setFormDetailsError(getVariable("request_form.error_generic", "Bir hata oluştu"));
            })
            .finally(() => setFormDetailsLoading(false));
        return () => { controller.abort(); if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current); };
    }, [productId, productType, languageId, initialFormId]);

    // Captcha widget (v2 / Turnstile) – RequestForm ile aynı
    useEffect(() => {
        if (captchaConfigLoading || !captchaConfig?.is_enabled || !captchaConfig?.site_key || !captchaConfig?.provider) return;
        const provider = captchaConfig.provider;
        const siteKey = captchaConfig.site_key;
        const scriptUrl = captchaConfig.script_url;
        const initCaptcha = async () => {
            try {
                if (provider === "google_recaptcha_v2") {
                    await loadRecaptchaScript(scriptUrl || undefined);
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById(YACHT_CAPTCHA_CONTAINER_ID);
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderRecaptchaWidget(YACHT_CAPTCHA_CONTAINER_ID, siteKey, () => {}).then((id) => {
                                captchaWidgetIdRef.current = id;
                            }).catch(() => {});
                        }
                    }, 100);
                    setTimeout(() => clearInterval(checkContainer), 10000);
                } else if (provider === "google_recaptcha_v3" || provider === "google_recaptcha_enterprise") {
                    await loadRecaptchaV3Script(
                        siteKey,
                        provider === "google_recaptcha_enterprise" ? scriptUrl ?? undefined : undefined
                    );
                } else if (provider === "cloudflare_turnstile") {
                    await loadTurnstileScript(scriptUrl || undefined);
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById(YACHT_CAPTCHA_CONTAINER_ID);
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderTurnstileWidget(YACHT_CAPTCHA_CONTAINER_ID, siteKey, () => {}).then((id) => {
                                captchaWidgetIdRef.current = id;
                            }).catch(() => {});
                        }
                    }, 100);
                    setTimeout(() => clearInterval(checkContainer), 10000);
                }
            } catch {}
        };
        const t = setTimeout(initCaptcha, 500);
        return () => {
            clearTimeout(t);
            if (captchaWidgetIdRef.current != null && provider === "google_recaptcha_v2") {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaWidgetIdRef.current != null && provider === "cloudflare_turnstile") {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
                removeTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        };
    }, [captchaConfig, captchaConfigLoading]);

    const requiredAgreements = formDetails?.agreements?.required ?? [];
    const optionalAgreements = formDetails?.agreements?.optional ?? [];
    const allAgreementsAccepted = requiredAgreements.every((a) => agreementsState[a.id] === true);
    const fields = formDetails?.fields ?? [];
    const fieldValidation = useMemo(() => validateDynamicFormFields(fields, formValues), [fields, formValues]);
    const submitReadiness = useFormSubmitReadiness({
        formLoading: formDetailsLoading,
        captchaLoading: captchaConfigLoading,
    });
    const isFormValid =
        allAgreementsAccepted && fieldValidation.valid && submitReadiness.canSubmit && fields.length > 0;

    const handleFieldChange = (name: string, value: string) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formDetails || isSubmitting) return;
        const formEl = e.currentTarget;
        if (formEl instanceof HTMLFormElement && !formEl.reportValidity()) {
            return;
        }
        const validation = validateDynamicFormFields(fields, formValues);
        if (!validation.valid) {
            setSubmitError(
                t(
                    "request_form.validation_required",
                    language === "tr" ? "Lütfen zorunlu alanları doldurun." : "Please fill in all required fields.",
                ),
            );
            return;
        }
        if (!allAgreementsAccepted) {
            setSubmitError(
                t(
                    "request_form.validation_agreements",
                    language === "tr" ? "Lütfen zorunlu sözleşmeleri onaylayın." : "Please accept the required agreements.",
                ),
            );
            return;
        }
        if (!submitReadiness.canSubmit) return;
        setSubmitError(null);

        let recaptchaToken = "";
        if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
            if (captchaConfig.provider === "google_recaptcha_v2") {
                recaptchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || "";
                if (!recaptchaToken) {
                    setSubmitError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
                    return;
                }
            } else if (captchaConfig.provider === "google_recaptcha_v3" || captchaConfig.provider === "google_recaptcha_enterprise") {
                try {
                    await loadRecaptchaV3Script(
                        captchaConfig.site_key,
                        captchaConfig.provider === "google_recaptcha_enterprise" ? captchaConfig.script_url ?? undefined : undefined
                    );
                    recaptchaToken = await getRecaptchaV3Token(captchaConfig.site_key, "yacht_request_form");
                } catch {
                    setSubmitError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
                    return;
                }
            } else if (captchaConfig.provider === "cloudflare_turnstile") {
                recaptchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || "";
                if (!recaptchaToken) {
                    setSubmitError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
                    return;
                }
            }
        }

        setIsSubmitting(true);
        const payload: Record<string, unknown> = {
            form_id: formDetails.id,
            product_id: productId,
            product_type: productType,
            check_in_date: checkInDate ? format(checkInDate, "yyyy-MM-dd") : "",
            check_out_date: checkOutDate ? format(checkOutDate, "yyyy-MM-dd") : "",
            guest_count: guestCount,
            ...formValues,
            newsletter_subscribed: newsletterSubscribed,
            agreements: [
                ...requiredAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: true })),
                ...optionalAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: false })),
            ],
        };
        if (languageId != null) payload.language_id = languageId;
        if (recaptchaToken) payload.recaptcha_token = recaptchaToken;
        try {
            const res = await fetch("/api/form/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setSubmitError(data?.message ?? (Array.isArray(data?.errors) ? Object.values(data.errors).flat().join(" ") : null) ?? t("request_form.error_generic"));
                if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                }
                return;
            }
            setSubmitSuccess(true);
            if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        } catch {
            setSubmitError(t("request_form.error_generic"));
            if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (formDetailsLoading) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-alsero-first" />
                    <span className="ml-2 text-alsero-dark-gray text-sm">{t("reservation.calculating_price")}</span>
                </div>
            </div>
        );
    }
    if (formDetailsError || !formDetails) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                    <p className="text-amber-800 text-sm">{formDetailsError ?? t("request_form.no_form_defined")}</p>
                </div>
            </div>
        );
    }
    if (submitSuccess) {
        return (
            <div className={`p-5 ${className}`}>
                <div className="rounded-lg border border-green-200 bg-green-50 p-5 text-center">
                    <p className="text-green-800 font-medium">{t("request_form.success_message")}</p>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={`p-5 space-y-5 ${className}`} autoComplete="off">
            {/* Title + Name + Surname yan yana (tur talep formu ile aynı) */}
            {fields.some((f) => (f.name || "").toLowerCase() === "title") &&
                fields.some((f) => (f.name || "").toLowerCase() === "name") &&
                fields.some((f) => (f.name || "").toLowerCase() === "surname") && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {fields
                        .filter((f) => ["title", "name", "surname"].includes((f.name || "").toLowerCase()))
                        .sort((a, b) => {
                            const order = ["title", "name", "surname"];
                            return order.indexOf((a.name || "").toLowerCase()) - order.indexOf((b.name || "").toLowerCase());
                        })
                        .map((field: FormField) => {
                            const name = field.name || field.field || "";
                            const label = field.label || field.title || name;
                            const required = field.required !== false && (field.required === true || field.required === "true" || field.required === 1);
                            const value = formValues[name] ?? "";
                            if ((field.type === "select" || name.toLowerCase() === "title") && (field.values?.length || field.options?.length)) {
                                const opts = field.values
                                    ? field.values.map((v) => ({ value: v.value ?? "", label: v.label ?? v.value ?? "" }))
                                    : (field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value }));
                                const options = !required ? [{ value: "", label: field.placeholder || t("request_form.select_placeholder", "Seçiniz") }, ...opts] : opts;
                                const selected = value ? options.find((o) => o.value === value) ?? null : null;
                                return (
                                    <div key={name} className="space-y-1.5">
                                        <label htmlFor={`yacht-request-${name}`} className={labelClassSmall}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                        <ReactSelect
                                            inputId={`yacht-request-${name}`}
                                            value={selected}
                                            onChange={(opt) => handleFieldChange(name, (opt as { value: string } | null)?.value ?? "")}
                                            options={options}
                                            placeholder={field.placeholder || t("request_form.select_placeholder", "Seçiniz")}
                                            isClearable={!required}
                                            isSearchable
                                            styles={searchableSelectStyles}
                                            noOptionsMessage={() => t("request_form.no_options", "Seçenek yok")}
                                            classNamePrefix="yacht-request-select"
                                        />
                                    </div>
                                );
                            }
                            if (name.toLowerCase() === "title" && field.type === "select") {
                                const defaultTitleOptions = ["Mr", "Mrs", "Ms", "Miss", "Dr.", "Other"].map((x) => ({ value: x.toLowerCase().replace(".", ""), label: x }));
                                const options = !required ? [{ value: "", label: field.placeholder || t("request_form.select_placeholder", "Seçiniz") }, ...defaultTitleOptions] : defaultTitleOptions;
                                const selected = value ? options.find((o) => o.value === value) ?? null : null;
                                return (
                                    <div key={name} className="space-y-1.5">
                                        <label htmlFor={`yacht-request-${name}`} className={labelClassSmall}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                        <ReactSelect
                                            inputId={`yacht-request-${name}`}
                                            value={selected}
                                            onChange={(opt) => handleFieldChange(name, (opt as { value: string } | null)?.value ?? "")}
                                            options={options}
                                            placeholder={field.placeholder || t("request_form.select_placeholder", "Seçiniz")}
                                            isClearable={!required}
                                            isSearchable
                                            styles={searchableSelectStyles}
                                            noOptionsMessage={() => t("request_form.no_options", "Seçenek yok")}
                                            classNamePrefix="yacht-request-select"
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div key={name} className="space-y-1.5">
                                    <label className={labelClassSmall}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                    <input type="text" name={name} required={required} value={value} onChange={(e) => handleFieldChange(name, e.target.value)} placeholder={field.placeholder} className={inputClass} />
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Sadece name + surname (title yoksa) */}
            {!fields.some((f) => (f.name || "").toLowerCase() === "title") &&
                fields.some((f) => (f.name || "").toLowerCase() === "name") &&
                fields.some((f) => (f.name || "").toLowerCase() === "surname") && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {fields.filter((f) => ["name", "surname"].includes((f.name || "").toLowerCase())).map((field: FormField) => {
                        const name = field.name || field.field || "";
                        const label = field.label || field.title || name;
                        const required = field.required !== false && (field.required === true || field.required === "true" || field.required === 1);
                        const value = formValues[name] ?? "";
                        return (
                            <div key={name} className="space-y-1.5">
                                <label className={labelClassSmall}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                <input type="text" name={name} required={required} value={value} onChange={(e) => handleFieldChange(name, e.target.value)} placeholder={field.placeholder} className={inputClass} />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Tarihler: tur talep formu ile aynı grid (2 sütun) */}
            <div className="space-y-4 pb-4 border-b border-alsero-soft-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DatePickerCustom
                        label={t("yacht_request.departure_date", language === "tr" ? "Çıkış Tarihi" : "Departure Date")}
                        labelClassName={labelClass}
                        selected={checkInDate}
                        onSelect={setCheckInDate}
                        disabled={checkInDisabled}
                        defaultMonth={checkInMinDate}
                        startMonth={periodStart ?? undefined}
                        endMonth={periodEnd ?? undefined}
                        language={language}
                    />
                    <DatePickerCustom
                        label={t("yacht_request.return_date", language === "tr" ? "Dönüş Tarihi" : "Return Date")}
                        labelClassName={labelClass}
                        selected={checkOutDate}
                        onSelect={setCheckOutDate}
                        disabled={checkOutDisabled}
                        defaultMonth={checkOutMinDate ?? checkInDate ?? checkInMinDate}
                        startMonth={periodStart ?? undefined}
                        endMonth={periodEnd ?? undefined}
                        language={language}
                    />
                </div>
                {minRentalDuration != null && minRentalDuration > 0 && (
                    <p className="text-xs text-alsero-dark-gray">
                        {t("yacht_request.min_rental_hint", language === "tr" ? "Minimum kiralama süresi" : "Minimum rental period")}: {minRentalDuration} {t("yacht_request.days", language === "tr" ? "gün" : "days")}
                        {String(rentalType || "").toLowerCase() === "weekly" && (
                            <span className="ml-1">({t("yacht_request.weekly_charter", language === "tr" ? "haftalık kiralama" : "weekly charter")})</span>
                        )}
                        {String(rentalType || "").toLowerCase() === "monthly" && (
                            <span className="ml-1">({t("yacht_request.monthly_charter", language === "tr" ? "aylık kiralama" : "monthly charter")})</span>
                        )}
                    </p>
                )}
                <div className="space-y-1.5">
                    <label className={labelClass}>{t("yacht_request.guest_count", language === "tr" ? "Misafir sayısı" : "Number of Guests")}</label>
                    <input
                        type="number"
                        min={1}
                        max={99}
                        value={guestCount}
                        onChange={(e) => setGuestCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        className={inputClass}
                    />
                </div>
            </div>

            {fields
                .filter((f) => !["check_in_date", "check_out_date", "guest_count", "title", "name", "surname"].includes((f.name || f.field || "").toLowerCase()))
                .map((field: FormField, index: number) => {
                    const name = field.name || field.field || `field_${index}`;
                    const label = field.label || field.title || name;
                    const required = field.required !== false && (field.required === true || field.required === "true" || field.required === 1);
                    const value = formValues[name] ?? "";
                    if (field.type === "textarea" || field.type === "textbox") {
                        return (
                            <div key={name} className="space-y-1.5">
                                <label className={labelClass}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                <textarea name={name} required={required} rows={field.rows ?? 4} value={value} onChange={(e) => handleFieldChange(name, e.target.value)} placeholder={field.placeholder} className={`${inputClass} resize-y min-h-[100px]`} />
                            </div>
                        );
                    }
                    if (field.type === "email" || name.toLowerCase().includes("email")) {
                        return (
                            <div key={name} className="space-y-1.5">
                                <label className={labelClass}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                <input type="email" name={name} required={required} value={value} onChange={(e) => handleFieldChange(name, e.target.value)} placeholder={field.placeholder} className={inputClass} />
                            </div>
                        );
                    }
                    if (field.type === "tel" || name.toLowerCase().includes("mobile") || name.toLowerCase().includes("phone")) {
                        return (
                            <div key={name} className="space-y-1.5">
                                <label htmlFor={`yacht-request-${name}`} className={labelClass}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                <PhoneInput
                                    name={name}
                                    defaultCountry="tr"
                                    value={value}
                                    onChange={(phoneValue) => handleFieldChange(name, phoneValue)}
                                    placeholder={field.placeholder || t("request_form.phone_placeholder", "Mobile Phone")}
                                    className="border border-[rgba(0,0,0,0.08)] focus-within:border-[rgba(0,0,0,0.40)] transition-all duration-300 rounded-lg !text-[#4F545A] font-medium text-sm px-2 py-2"
                                    inputClassName="!border-0 w-full"
                                    countrySelectorStyleProps={{
                                        buttonClassName: "!border-0 !px-2 !rounded-lg transition-all duration-300 hover:bg-gray-50",
                                        dropdownStyleProps: {
                                            className: "!bg-white !border !border-[rgba(0,0,0,0.08)] !rounded-lg !shadow-lg !mt-1 !max-h-64 !overflow-y-auto",
                                            listItemClassName: "!px-3 !py-2 hover:!bg-alsero-soft-white !transition-colors !duration-200 !cursor-pointer !flex !items-center !gap-2",
                                            listItemFlagClassName: "!text-lg !mr-2",
                                            listItemCountryNameClassName: "!text-gray-700 !font-medium !text-sm",
                                            listItemDialCodeClassName: "!text-gray-500 !text-sm !ml-auto",
                                        },
                                    }}
                                />
                            </div>
                        );
                    }
                    if (field.type === "country" || name === "country_id" || (name.toLowerCase().includes("country") && name !== "country")) {
                        const countryOptions = [{ value: "", label: field.placeholder || t("request_form.country_placeholder", "Ülke seçiniz") }, ...countries.map((c) => ({ value: String(c.id), label: c.name }))];
                        const selectedCountry = value ? countryOptions.find((o) => o.value === value) ?? null : null;
                        return (
                            <div key={name} className="space-y-1.5">
                                <label htmlFor={`yacht-request-${name}`} className={labelClass}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                                <ReactSelect
                                    inputId={`yacht-request-${name}`}
                                    value={selectedCountry}
                                    onChange={(opt) => handleFieldChange(name, (opt as { value: string } | null)?.value ?? "")}
                                    options={countryOptions}
                                    placeholder={field.placeholder || t("request_form.country_placeholder", "Ülke seçiniz")}
                                    isClearable={!required}
                                    isSearchable
                                    styles={searchableSelectStyles}
                                    noOptionsMessage={() => t("request_form.no_options", "Seçenek yok")}
                                    loadingMessage={() => t("request_form.loading", "Yükleniyor...")}
                                    classNamePrefix="yacht-request-select"
                                    components={{ Input: NoAutocompleteInput }}
                                />
                            </div>
                        );
                    }
                    return (
                        <div key={name} className="space-y-1.5">
                            <label className={labelClass}>{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
                            <input type={field.type === "number" ? "number" : "text"} name={name} required={required} value={value} onChange={(e) => handleFieldChange(name, e.target.value)} placeholder={field.placeholder} className={inputClass} />
                        </div>
                    );
                })}

            {(requiredAgreements.length > 0 || optionalAgreements.length > 0) && (
                <div className="space-y-3 pt-4 border-t border-[rgba(0,0,0,0.08)]">
                    <h4 className="text-alsero-dark-gray text-sm font-semibold">{t("reservation.agreements", "Sözleşmeler")}</h4>
                    {requiredAgreements.length > 0 && (
                        <div className="text-alsero-dark-gray text-sm">
                            {requiredAgreements.map((agreement, index) => (
                                <span key={agreement.id}>
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedAgreement(agreement); setAgreementModalOpen(true); }}
                                        className="text-alsero-first hover:underline font-semibold"
                                    >
                                        {agreement.title}
                                    </button>
                                    {index < requiredAgreements.length - 1 ? (
                                        <span className="text-alsero-dark-gray"> {t("request_form.agreement_separator", "buradan, ")} </span>
                                    ) : (
                                        <span className="text-alsero-dark-gray"> {t("request_form.agreement_suffix", "buradan ")} </span>
                                    )}
                                </span>
                            ))}
                            {t("request_form.agreement_end", "ulaşabilirsiniz.")}
                        </div>
                    )}
                    {optionalAgreements.map((agreement) => (
                        <div key={agreement.id} className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                id={`agreement-opt-${agreement.id}`}
                                checked={agreementsState[agreement.id] ?? false}
                                onChange={(e) => setAgreementsState((prev) => ({ ...prev, [agreement.id]: e.target.checked }))}
                                className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-alsero-soft-white rounded cursor-pointer"
                            />
                            <label htmlFor={`agreement-opt-${agreement.id}`} className="flex-1 text-xs text-alsero-dark-gray cursor-pointer">
                                <button
                                    type="button"
                                    onClick={() => { setSelectedAgreement(agreement); setAgreementModalOpen(true); }}
                                    className="text-alsero-first hover:underline font-semibold"
                                >
                                    {agreement.title}
                                </button>
                                {t("request_form.agreement_optional_confirm", "'ni okudum, onaylıyorum.")}
                            </label>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-start gap-2 pt-2">
                <input
                    type="checkbox"
                    id="yacht-request-form-newsletter"
                    checked={newsletterSubscribed}
                    onChange={(e) => setNewsletterSubscribed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-alsero-soft-white rounded cursor-pointer"
                    aria-describedby="yacht-request-form-newsletter-label"
                />
                <label id="yacht-request-form-newsletter-label" htmlFor="yacht-request-form-newsletter" className="flex-1 text-sm text-alsero-dark-gray cursor-pointer">
                    {t("form.newsletter_subscribe_label", "I would like to subscribe to the newsletter. You can check this to receive campaigns and news by email.")}
                </label>
            </div>

            {/* Robot doğrulama (RequestForm ile aynı) */}
            {captchaConfigLoading && (
                <div className="flex items-center gap-2 text-sm text-alsero-dark-gray">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-alsero-first border-t-transparent" />
                    {t("request_form.captcha_loading", language === "tr" ? "Doğrulama yükleniyor..." : "Verification loading...")}
                </div>
            )}
            {!captchaConfigLoading && captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider && (
                <>
                    {(captchaConfig.provider === "google_recaptcha_v2" || captchaConfig.provider === "cloudflare_turnstile") && (
                        <div id={YACHT_CAPTCHA_CONTAINER_ID} className="min-h-[78px]" />
                    )}
                    {(captchaConfig.provider === "google_recaptcha_v3" || captchaConfig.provider === "google_recaptcha_enterprise") && (
                        <div className="space-y-1.5">
                            <p className="text-sm text-alsero-dark-gray/80">{t("common.captcha_auto_note", language === "tr" ? "Robot doğrulaması gönder sırasında otomatik yapılır." : "Verification is done automatically when you submit.")}</p>
                            <RecaptchaLegalNotice language={language} />
                        </div>
                    )}
                </>
            )}

            {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-red-600 text-sm">{submitError}</p>
                </div>
            )}

            <button
                type="submit"
                disabled={!isFormValid || isSubmitting || formDetailsLoading}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${isFormValid && !isSubmitting ? "bg-alsero-first hover:bg-alsero-first/90 focus:outline-none focus:ring-2 focus:ring-alsero-first/20" : "bg-gray-400 cursor-not-allowed"}`}
            >
                {isSubmitting ? t("request_form.submitting") : t("request_form.submit")}
            </button>

            {selectedAgreement && (
                <AgreementModal
                    isOpen={agreementModalOpen}
                    onClose={() => { setAgreementModalOpen(false); setSelectedAgreement(null); }}
                    agreementSlug={selectedAgreement.slug}
                    language={language}
                    agreementId={selectedAgreement.id}
                    isRequired={selectedAgreement.is_required}
                    onAccept={(agreementId) => setAgreementsState((prev) => ({ ...prev, [agreementId]: true }))}
                />
            )}
        </form>
    );
}
